// Shared look for the library pages - colors, cards, book covers and pills.
import React from 'react';
import { Box, Typography, ButtonBase, Paper } from '@mui/material';

export const FONT = 'Cairo, Tajawal, sans-serif';

export const C = {
  primary: '#4338CA',
  primarySoft: '#EEF2FF',
  violet: '#5B21F4',
  sky: '#00BFFF',
  bg: '#F8F9FF',
  border: '#E5E7EB',
  text: '#1E1B4B',
  muted: '#6B7280',
  success: '#059669',
  successSoft: '#ECFDF5',
  warning: '#D97706',
  warningSoft: '#FFFBEB',
  danger: '#DC2626',
  dangerSoft: '#FEF2F2',
};

export const TONES = {
  primary: { fg: C.primary, bg: C.primarySoft },
  success: { fg: C.success, bg: C.successSoft },
  warning: { fg: C.warning, bg: C.warningSoft },
  danger: { fg: C.danger, bg: C.dangerSoft },
  neutral: { fg: C.muted, bg: '#F3F4F6' },
};

export const HEADER_GRADIENT = 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)';

// Rounded inputs that match the rest of the app
export const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#fff',
    fontFamily: FONT,
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.primary },
    '&.Mui-focused fieldset': { borderColor: C.primary },
  },
  '& .MuiInputLabel-root': { fontFamily: FONT },
};

export const primaryButtonSx = {
  borderRadius: 2.5,
  px: 2.5,
  fontFamily: FONT,
  fontWeight: 700,
  textTransform: 'none',
  boxShadow: 'none',
  bgcolor: C.primary,
  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
};

export const dialogPaperProps = {
  sx: { borderRadius: 4, fontFamily: FONT, direction: 'rtl' },
};

export const dialogTitleSx = { fontFamily: FONT, fontWeight: 800, color: C.text };

export const Panel = ({ title, subtitle, action, children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 4,
      bgcolor: '#fff',
      border: `1px solid ${C.border}`,
      boxShadow: '0 2px 12px rgba(67,56,202,0.05)',
      ...sx,
    }}
  >
    {(title || action) && (
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={2}>
        <Box>
          {title && (
            <Typography fontWeight={800} color={C.text} sx={{ fontFamily: FONT }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" color={C.muted} sx={{ fontFamily: FONT }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Paper>
);

export const StatTile = ({ label, value, hint, icon, tone = 'primary' }) => {
  const t = TONES[tone];
  return (
    <Panel sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 48, height: 48, borderRadius: 3, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: t.bg, color: t.fg,
        }}
      >
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography variant="h5" fontWeight={800} color={C.text} lineHeight={1.2} sx={{ fontFamily: FONT }}>
          {value}
        </Typography>
        <Typography variant="body2" color={C.muted} sx={{ fontFamily: FONT }}>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" color={t.fg} sx={{ fontFamily: FONT }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Panel>
  );
};

// Responsive grid without the MUI Grid API
export const TileGrid = ({ min = 220, gap = 2, children, sx }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))`,
      gap,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const COVER_COLORS = [
  ['#4338CA', '#6366F1'],
  ['#0EA5E9', '#38BDF8'],
  ['#059669', '#34D399'],
  ['#D97706', '#FBBF24'],
  ['#DB2777', '#F472B6'],
  ['#7C3AED', '#A78BFA'],
  ['#0F766E', '#2DD4BF'],
  ['#B91C1C', '#F87171'],
];

const hash = (text = '') => [...text].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7);

// A generated book cover: the same title always gets the same color
export const BookCover = ({ title = '', width = 56 }) => {
  const [dark, light] = COVER_COLORS[hash(title) % COVER_COLORS.length];
  const height = Math.round(width * 1.4);
  const initials = title.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join(' ');
  return (
    <Box
      sx={{
        width, height, flexShrink: 0,
        borderRadius: '4px 10px 10px 4px',
        background: `linear-gradient(160deg, ${light} 0%, ${dark} 100%)`,
        boxShadow: `inset -6px 0 0 rgba(0,0,0,0.18), 0 4px 10px ${dark}40`,
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', top: '18%', left: 8, right: 10, height: 2, bgcolor: 'rgba(255,255,255,0.35)' }} />
      <Box sx={{ position: 'absolute', bottom: '18%', left: 8, right: 10, height: 2, bgcolor: 'rgba(255,255,255,0.35)' }} />
      <Typography
        sx={{ fontFamily: FONT, fontWeight: 800, fontSize: width / 3.2, lineHeight: 1, pr: '6px' }}
      >
        {initials || '📖'}
      </Typography>
    </Box>
  );
};

export const Pill = ({ label, tone = 'neutral', icon }) => {
  const t = TONES[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: 1.25, py: 0.25, borderRadius: 999,
        bgcolor: t.bg, color: t.fg,
        fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {label}
    </Box>
  );
};

// Pill-shaped filter buttons, optionally with a count
export const FilterChips = ({ options, value, onChange }) => (
  <Box display="flex" gap={1} flexWrap="wrap">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <ButtonBase
          key={opt.value}
          onClick={() => onChange(opt.value)}
          sx={{
            px: 1.75, py: 0.75, borderRadius: 999,
            fontFamily: FONT, fontWeight: 700, fontSize: '0.85rem',
            border: `1px solid ${active ? C.primary : C.border}`,
            bgcolor: active ? C.primary : '#fff',
            color: active ? '#fff' : C.text,
            transition: 'all .15s',
            '&:hover': { borderColor: C.primary },
          }}
        >
          {opt.label}
          {opt.count !== undefined && (
            <Box
              component="span"
              sx={{
                mr: 1, px: 0.75, borderRadius: 999, fontSize: '0.75rem',
                bgcolor: active ? 'rgba(255,255,255,0.25)' : C.primarySoft,
                color: active ? '#fff' : C.primary,
              }}
            >
              {opt.count}
            </Box>
          )}
        </ButtonBase>
      );
    })}
  </Box>
);

export const EmptyState = ({ icon, title, hint, action }) => (
  <Box
    sx={{
      textAlign: 'center', py: 6, px: 2,
      border: `2px dashed ${C.border}`, borderRadius: 4, bgcolor: '#fff',
    }}
  >
    <Box sx={{ color: C.primary, opacity: 0.6, mb: 1, '& svg': { fontSize: 48 } }}>{icon}</Box>
    <Typography fontWeight={800} color={C.text} sx={{ fontFamily: FONT }}>{title}</Typography>
    {hint && (
      <Typography variant="body2" color={C.muted} sx={{ fontFamily: FONT, mt: 0.5 }}>{hint}</Typography>
    )}
    {action && <Box mt={2}>{action}</Box>}
  </Box>
);

// Horizontal bar list for simple breakdowns
export const BarList = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {rows.map((row) => (
        <Box key={row.label}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color={C.text} sx={{ fontFamily: FONT }}>{row.label}</Typography>
            <Typography variant="body2" fontWeight={800} color={C.text} sx={{ fontFamily: FONT }}>{row.value}</Typography>
          </Box>
          <Box sx={{ height: 8, borderRadius: 999, bgcolor: C.primarySoft, overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%', width: `${(row.value / max) * 100}%`,
                borderRadius: 999, background: `linear-gradient(90deg, ${C.violet}, ${C.primary})`,
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB') : '-');

export const isOverdue = (transaction) =>
  transaction.status === 'borrowed' && new Date(transaction.due_date) < new Date(new Date().toDateString());
